import React, { useState, useEffect } from 'react';
import { FormattedTime } from '../components/FormattedDate';
import AttendanceStrategyFactory from '../services/attendance/AttendanceStrategyFactory';
import './AttendanceCalendar.css';

const AttendanceCalendar = ({
     days,
     monthRange,
     attendanceSummary,
     holidays = [],
     onMonthChange,
     weekOffDay,
     employeeType = "Contractual_Employee"
}) => {
     const [hoveredDay, setHoveredDay] = useState(null);
     const [popupDetails, setPopupDetails] = useState({ show: false, punchIn: '', punchOut: '', day: null, position: {}, workHours: '' });
     const [sundaysCount, setSundaysCount] = useState(0);
     const [holidaysCount, setHolidaysCount] = useState(0);
     const [selectedMonth, setSelectedMonth] = useState("");
     const [attendanceStrategy, setAttendanceStrategy] = useState(null);

     const dayNameToNumber = {
          Sunday: 0,
          Monday: 1,
          Tuesday: 2,
          Wednesday: 3,
          Thursday: 4,
          Friday: 5,
          Saturday: 6,
     };
     const weekOffDayNumber = dayNameToNumber[weekOffDay] ?? 0;

     useEffect(() => {
          setSelectedMonth(
               `${new Date(monthRange.start).getFullYear()}-${String(new Date(monthRange.start).getMonth() + 1).padStart(2, '0')}`
          );
     }, [monthRange]);

     useEffect(() => {
          try {
               const strategy = AttendanceStrategyFactory.getStrategy(employeeType);
               setAttendanceStrategy(strategy);
          } catch {
               setAttendanceStrategy(AttendanceStrategyFactory.getStrategy("Contractual_Employee"));
          }
     }, [employeeType]);

     const getDaysInMonth = () => {
          const startDate = new Date(monthRange.start);
          return new Date(startDate.getFullYear(), startDate.getMonth() + 1, 0).getDate();
     };

     const getStartDayOfWeek = () => {
          const startDate = new Date(monthRange.start);
          return startDate.getDay();
     };

     // Calculate working hours in HH:MM format (without seconds)
     const calculateWorkingHours = (punchIn, punchOut) => {
          if (!punchIn || !punchOut) return '00:00';
          const punchInDate = new Date(punchIn);
          const punchOutDate = new Date(punchOut);
          const diffMs = punchOutDate - punchInDate;
          if (diffMs <= 0) return '00:00';
          const totalMinutes = Math.floor(diffMs / (1000 * 60));
          const hours = Math.floor(totalMinutes / 60);
          const minutes = totalMinutes % 60;
          return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
     };

     // Format working hours to HH:MM (remove seconds if present)
     const formatWorkingHours = (workHours) => {
          if (!workHours || workHours === '-') return '-';
          // If it contains seconds (HH:MM:SS), take only HH:MM
          const parts = workHours.split(':');
          if (parts.length >= 2) {
               return `${parts[0]}:${parts[1]}`;
          }
          return workHours;
     };

     // Prepare holiday map
     const holidayDatesMap = {};
     holidays.forEach(h => {
          holidayDatesMap[h.holiday_date] = h.holiday_name;
     });

     useEffect(() => {
          const daysInMonth = getDaysInMonth();
          let sundayCounter = 0;
          let holidayCounter = 0;

          for (let d = 1; d <= daysInMonth; d++) {
               const year = new Date(monthRange.start).getFullYear();
               const month = new Date(monthRange.start).getMonth() + 1;
               const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
               const dateObj = new Date(year, month - 1, d);

               const dayStatus = days.find(day => {
                    const dayDate = new Date(day.date);
                    return (
                         dayDate.getFullYear() === year &&
                         dayDate.getMonth() + 1 === month &&
                         dayDate.getDate() === d
                    );
               });

               if (!dayStatus) {
                    if (dateObj.getDay() === weekOffDayNumber) {
                         sundayCounter++;
                    } else if (holidayDatesMap[dateStr]) {
                         holidayCounter++;
                    }
               }
          }

          setSundaysCount(sundayCounter);
          setHolidaysCount(holidayCounter);
     }, [monthRange, days, holidays]);

     const calculateAttendanceStatus = (day, roster, holidays) => {
          if (!attendanceStrategy) {
               const workHours = day.punch_in && day.punch_out ? calculateWorkingHours(day.punch_in, day.punch_out) : "-";
               return {
                    status: day.punch_in ? "Present" : "Absent",
                    lateMark: "-",
                    workingHours: workHours,
                    statusColor: day.punch_in ? "#10b981" : "#ef4444",
                    remarks: ""
               };
          }
          try {
               const employeeData = {
                    weekAttendance: [weekOffDay],
                    allDays: days,
                    region: "Other"
               };
               const result = attendanceStrategy.calculateAttendanceStatus(day, roster, holidays, employeeData);
               // Format working hours to HH:MM
               if (result.workingHours && result.workingHours !== '-') {
                    result.workingHours = formatWorkingHours(result.workingHours);
               }
               return result;
          } catch {
               const workHours = day.punch_in && day.punch_out ? calculateWorkingHours(day.punch_in, day.punch_out) : "-";
               return {
                    status: day.punch_in ? "Present" : "Absent",
                    lateMark: "-",
                    workingHours: workHours,
                    statusColor: day.punch_in ? "#10b981" : "#ef4444",
                    remarks: ""
               };
          }
     };

     // const renderCalendar = () => {
     //      const daysInMonth = getDaysInMonth();
     //      const startDayOfWeek = getStartDayOfWeek();
     //      const totalCells = 42;
     //      const calendar = [];

     //      for (let i = 0; i < totalCells; i++) {
     //           const currentDay = i - startDayOfWeek + 1;
     //           let punchIn = null;
     //           let punchOut = null;
     //           let holidayName = null;
     //           let workingHours = null;
     //           let status = null;
     //           let statusColor = null;

     //           if (currentDay > 0 && currentDay <= daysInMonth) {
     //                const year = new Date(monthRange.start).getFullYear();
     //                const month = new Date(monthRange.start).getMonth() + 1;
     //                const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(currentDay).padStart(2, '0')}`;
     //                const dayStatus = days.find(day => day.date === dateStr);
     //                const roster = attendanceSummary?.duty_roster?.find(r => r.attendance_date === dateStr);
     //                const holidayObj = holidays.find(h => h.holiday_date === dateStr);
     //                holidayName = holidayObj?.holiday_name;

     //                const attendanceResult = calculateAttendanceStatus(
     //                     dayStatus || { date: dateStr, punch_in: null, punch_out: null },
     //                     roster,
     //                     holidays
     //                );
     //                status = attendanceResult.status;
     //                statusColor = attendanceResult.statusColor;
     //                workingHours = attendanceResult.workingHours;
     //                punchIn = dayStatus?.punch_in;
     //                punchOut = dayStatus?.punch_out;
     //           }

     //           const isEmptyCell = currentDay <= 0 || currentDay > daysInMonth;

     //           calendar.push(
     //                <div
     //                     key={i}
     //                     className={`calendar-day ${isEmptyCell ? 'empty-day' : ''}`}
     //                     onMouseEnter={(e) => !isEmptyCell && handleMouseEnter(e, currentDay, punchIn, punchOut, workingHours)}
     //                     onMouseLeave={handleMouseLeave}
     //                >
     //                     {!isEmptyCell && (
     //                          <>
     //                               {status && (
     //                                    <span
     //                                         className="status-dot"
     //                                         style={{
     //                                              backgroundColor: statusColor || (
     //                                                   status === "present" ? "green" :
     //                                                        status === "absent" ? "red" :
     //                                                             status === "leave" ? "blue" :
     //                                                                  status === "holiday" ? "#FFBF00" :
     //                                                                       status === "week_off" ? "#00BFFC" :
     //                                                                            status === "Half Day" ? "orange" :
     //                                                                                 status === "Early Going" ? "purple" : "transparent"
     //                                              )
     //                                         }}
     //                                    />
     //                               )}

     //                               <span className="day-number">{currentDay}</span>

     //                               {holidayName && (
     //                                    <div className="holiday-name">{holidayName}</div>
     //                               )}

     //                               <div className="day-details">
     //                                    {(status === "Week Off" || status === "Weekoff") && (
     //                                         <div className="status-text week-off-text">Weekly Off</div>
     //                                    )}
     //                                    {(punchIn || punchOut) && (
     //                                         <div className="time-info-row">
     //                                              {punchIn && <span className="time-in"><FormattedTime value={punchIn} /></span>}
     //                                              {punchIn && punchOut && <span className="time-separator">-</span>}
     //                                              {punchOut && <span className="time-out"><FormattedTime value={punchOut} /></span>}
     //                                         </div>
     //                                    )}
     //                                    {workingHours && workingHours !== "-" && workingHours !== "00:00" && (
     //                                         <div className="working-hours">
     //                                              {formatWorkingHours(workingHours)} Hrs
     //                                         </div>
     //                                    )}
     //                                    {status && status !== "Present" && status !== "Week Off" && status !== "Weekoff" && !holidayName && status !== "-" && (
     //                                         <div className="status-text">{status}</div>
     //                                    )}
     //                               </div>
     //                          </>
     //                     )}
     //                </div>
     //           );
     //      }

     //      return calendar;
     // };

     const renderCalendar = () => {
          const daysInMonth = getDaysInMonth();
          const startDayOfWeek = getStartDayOfWeek();
          const totalCells = 42;
          const calendar = [];
          const today = new Date();
          const currentDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

          for (let i = 0; i < totalCells; i++) {
               const currentDay = i - startDayOfWeek + 1;
               let punchIn = null;
               let punchOut = null;
               let holidayName = null;
               let workingHours = null;
               let status = null;
               let statusColor = null;
               let shiftInTime = null;
               let shiftOutTime = null;

               if (currentDay > 0 && currentDay <= daysInMonth) {
                    const year = new Date(monthRange.start).getFullYear();
                    const month = new Date(monthRange.start).getMonth() + 1;
                    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(currentDay).padStart(2, '0')}`;
                    const dayStatus = days.find(day => day.date === dateStr);
                    const roster = attendanceSummary?.duty_roster?.find(r => r.attendance_date === dateStr);
                    const holidayObj = holidays.find(h => h.holiday_date === dateStr);
                    holidayName = holidayObj?.holiday_name;

                    // Get shift times from roster
                    if (roster) {
                         shiftInTime = roster.in_time;
                         shiftOutTime = roster.out_time;
                    }

                    const attendanceResult = calculateAttendanceStatus(
                         dayStatus || { date: dateStr, punch_in: null, punch_out: null },
                         roster,
                         holidays
                    );
                    status = attendanceResult.status;
                    statusColor = attendanceResult.statusColor;
                    workingHours = attendanceResult.workingHours;
                    punchIn = dayStatus?.punch_in;
                    punchOut = dayStatus?.punch_out;

                    // ✅ Fix: Show "OnDuty" for current date if:
                    // 1. punch_out is null/empty, OR
                    // 2. punch_in and punch_out are the same
                    if (dateStr === currentDate && punchIn) {
                         if (!punchOut || punchIn === punchOut) {
                              status = "OnDuty";
                              statusColor = "#3b82f6"; // Blue color for OnDuty
                         }
                    }
               }

               const isEmptyCell = currentDay <= 0 || currentDay > daysInMonth;

               calendar.push(
                    <div
                         key={i}
                         className={`calendar-day ${isEmptyCell ? 'empty-day' : ''}`}
                         onMouseEnter={(e) => !isEmptyCell && handleMouseEnter(e, currentDay, punchIn, punchOut, workingHours, shiftInTime, shiftOutTime)}
                         onMouseLeave={handleMouseLeave}
                    >
                         {!isEmptyCell && (
                              <>
                                   {status && (
                                        <span
                                             className="status-dot"
                                             style={{
                                                  backgroundColor: statusColor || (
                                                       status === "present" ? "green" :
                                                            status === "absent" ? "red" :
                                                                 status === "leave" ? "blue" :
                                                                      status === "holiday" ? "#FFBF00" :
                                                                           status === "week_off" ? "#00BFFC" :
                                                                                status === "Half Day" ? "orange" :
                                                                                     status === "Early Going" ? "purple" :
                                                                                          status === "OnDuty" ? "#3b82f6" : "transparent"
                                                  )
                                             }}
                                        />
                                   )}

                                   <span className="day-number">{currentDay}</span>

                                   {holidayName && (
                                        <div className="holiday-name">{holidayName}</div>
                                   )}

                                   <div className="day-details">
                                        {(status === "Week Off" || status === "Weekoff") && (
                                             <div className="status-text week-off-text">Weekly Off</div>
                                        )}

                                        {/* Show shift times if available */}
                                        {shiftInTime && shiftOutTime && (
                                             <div className="shift-time-info" style={{ fontSize: '10px', color: '#6b7280', marginBottom: '2px' }}>
                                                  <span>🕐 {shiftInTime.substring(0, 5)} - {shiftOutTime.substring(0, 5)}</span>
                                             </div>
                                        )}

                                        {(punchIn || punchOut) && (
                                             <div className="time-info-row">
                                                  {punchIn && <span className="time-in"><FormattedTime value={punchIn} /></span>}
                                                  {punchIn && punchOut && punchIn !== punchOut && <span className="time-separator">-</span>}
                                                  {punchOut && punchIn !== punchOut && <span className="time-out"><FormattedTime value={punchOut} /></span>}
                                             </div>
                                        )}
                                        {workingHours && workingHours !== "-" && workingHours !== "00:00" && (
                                             <div className="working-hours">
                                                  {formatWorkingHours(workingHours)} Hrs
                                             </div>
                                        )}
                                        {status && status !== "Present" && status !== "Week Off" && status !== "Weekoff" && !holidayName && status !== "-" && (
                                             <div className="status-text">{status}</div>
                                        )}
                                   </div>
                              </>
                         )}
                    </div>
               );
          }

          return calendar;
     };

     const handleMouseEnter = (e, currentDay, punchIn, punchOut, workingHours) => {
          if (punchIn && punchOut) {
               const position = e.target.getBoundingClientRect();
               const formattedHours = workingHours || calculateWorkingHours(punchIn, punchOut);
               setPopupDetails({
                    show: true,
                    punchIn,
                    punchOut,
                    day: currentDay,
                    workHours: formatWorkingHours(formattedHours),
                    position: {
                         top: position.bottom + window.scrollY + 5,
                         left: position.left + window.scrollX,
                    }
               });
          }
     };

     const handleMouseLeave = () => {
          setPopupDetails({ show: false, punchIn: '', punchOut: '', day: null, position: {}, workHours: '' });
     };

     const handleMonthChange = (e) => {
          const newMonth = e.target.value;
          setSelectedMonth(newMonth);
          if (onMonthChange) {
               const [year, month] = newMonth.split('-');
               onMonthChange(new Date(Number(year), Number(month) - 1, 1));
          }
     };

     const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

     return (
          <div className="attendance-calendar-wrapper">
               <div className="calendar-header">
                    <input
                         type="month"
                         className="month-selector"
                         value={selectedMonth}
                         onChange={handleMonthChange}
                         min="2025-08"
                    />
               </div>

               <div className="calendar-container">
                    <div className="calendar-weekdays">
                         {daysOfWeek.map((day, index) => (
                              <div key={index} className="weekday-header">
                                   {day}
                              </div>
                         ))}
                    </div>
                    <div className="calendar-grid">
                         {renderCalendar()}
                    </div>
               </div>

               {popupDetails.show && (
                    <div
                         className="attendance-popup"
                         style={{
                              top: popupDetails.position.top,
                              left: popupDetails.position.left,
                         }}
                    >
                         <div><FormattedTime value={popupDetails.punchIn} /></div>
                         <div><FormattedTime value={popupDetails.punchOut} /></div>
                         <div className="popup-hours">
                              {popupDetails.workHours} Hrs
                         </div>
                    </div>
               )}

               <div className="calendar-legend">
                    <div className="legend-item present">
                         <span className="legend-dot"></span>
                         Present: {attendanceSummary.present}
                    </div>
                    <div className="legend-item absent">
                         <span className="legend-dot"></span>
                         Absent: {attendanceSummary.absent}
                    </div>
                    <div className="legend-item week-off">
                         <span className="legend-dot"></span>
                         Weekly Off: {sundaysCount}
                    </div>
                    <div className="legend-item holiday">
                         <span className="legend-dot"></span>
                         Holiday: {attendanceSummary.holiday}
                    </div>
               </div>
          </div>
     );
};

export default AttendanceCalendar;